import { useEffect, useRef, useState } from "react";

const POSTER = "/video/window-lt003-poster.jpg";
const DESCRIPTION = "Unedited laboratory recording LT-003: a rear-projected image on a clear sheet of coated plastic";
const CLASS_NAME = "h-full w-full object-cover object-center lg:object-contain";

export const WindowFilm = ({ onPlaying }) => {
  const videoRef = useRef(null);
  const [still] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData === true);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const video = videoRef.current;
    if (still || !video) return undefined;
    // Set the silent/inline properties before attaching any media, as in HeroVideo.
    video.defaultMuted = true;
    video.muted = true;
    video.volume = 0;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "true");
    const width = window.matchMedia("(max-width: 768px)").matches ? 720 : 1080;
    const sources = [
      { src: `/video/window-lt003-${width}.mp4`, type: `video/mp4; codecs="avc1.6400${width === 720 ? "1f" : "32"}"` },
      { src: `/video/window-lt003-${width}.webm`, type: 'video/webm; codecs="vp9"' },
    ];
    const source = sources.find(({ type }) => video.canPlayType(type));
    if (!source) { setStatus("error"); return undefined; }

    let disposed = false;
    let pending = false;
    let inView = true;
    const disarm = () => {
      window.removeEventListener("touchstart", gestureRetry);
      window.removeEventListener("pointerdown", gestureRetry);
    };
    const attempt = async () => {
      if (disposed || pending || !inView || document.visibilityState === "hidden") return;
      if (!video.paused && video.readyState >= 2) return;
      pending = true;
      video.muted = true;
      try {
        await video.play();
        if (!disposed) disarm();
      } catch (error) {
        if (disposed || error.name === "AbortError") return;
        const blocked = error.name === "NotAllowedError";
        setStatus(blocked ? "blocked" : "error");
        if (blocked) {
          window.addEventListener("touchstart", gestureRetry, { once: true, passive: true });
          window.addEventListener("pointerdown", gestureRetry, { once: true, passive: true });
        }
      } finally {
        pending = false;
      }
    };
    function gestureRetry() { disarm(); attempt(); }
    const playing = () => { setStatus("playing"); disarm(); onPlaying(); };
    const failed = () => { if (!disposed) setStatus("error"); };
    video.addEventListener("playing", playing);
    video.addEventListener("canplay", attempt);
    video.addEventListener("error", failed);
    window.addEventListener("pageshow", attempt);
    document.addEventListener("visibilitychange", attempt);
    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView) attempt();
    });
    observer.observe(video);
    // One supported source avoids an exhausted <source> list leaving play() pending.
    // Safari keeps MP4; browsers without H.264 use the identical VP9 loop.
    video.src = source.src;
    video.load();
    attempt();
    return () => {
      disposed = true;
      disarm();
      observer.disconnect();
      video.removeEventListener("playing", playing);
      video.removeEventListener("canplay", attempt);
      video.removeEventListener("error", failed);
      window.removeEventListener("pageshow", attempt);
      document.removeEventListener("visibilitychange", attempt);
      video.pause();
    };
  }, [still, onPlaying]);

  if (still) return <img data-testid="window-moment-still" src={POSTER} alt={DESCRIPTION} className={CLASS_NAME} />;
  return (
    <>
      <video ref={videoRef} data-testid="window-moment-video" data-playback-state={status}
        muted loop playsInline webkit-playsinline="true" autoPlay preload="auto" disablePictureInPicture
        poster={POSTER} aria-label={DESCRIPTION} className={CLASS_NAME} />
    </>
  );
};